/**
 * frontend/src/pages/valuation/_valuationTuningHelpers.ts
 *
 * Pure derivations for ValuationTuning.tsx, split out under
 * docs/FILE_SIZE_POLICY.md (the screen file was past the 350-line
 * "split first" threshold). No React, no state — these are re-run from
 * `useMemo` on the screen, not owners of anything themselves.
 */
import type { ConfigResponse, RecomputeBody, TuningBody, TuningKnobs } from '@/types/valuationB3'
import type { DriverQuestion, DriverQuestionnaire } from '@/types/valuation'
import type { DraftInputs } from '@/types/valuationDraft'

/**
 * VAL-NAV-STATE-1 R2 — the benchmark row Tuning runs against, derived from
 * the engagement's own signed industry profile (`benchmarkKeyForIndustry`,
 * `./_valuationIndustryMap`) rather than picked on this screen. Deliberately
 * smaller than `IndustrySelection` (`components/valuation/IndustryPicker`) —
 * that type's `label`/`provisional` fields described a picker's UI state
 * this screen no longer has; `naicsCode`/`subVertical` stay on the shape
 * because `/recompute` still accepts them, always `null` post-R2 since no
 * sub-vertical refinement UI remains once the picker is gone.
 */
export interface ResolvedIndustry {
  key: string
  naicsCode: string | null
  subVertical: string | null
}

/**
 * The B3 config carries the questions raw — no `max_points`, no section list.
 * Both are recovered from the options already on the wire rather than fetched
 * from /methodology, so the form always describes the config the run will
 * actually be scored on. (`max_points` is literally `max(option.points)` in
 * `config_schema.DriverQuestion`; this is the same derivation, not a second
 * opinion about it.)
 */
export function adaptQuestionnaire(config: ConfigResponse): DriverQuestionnaire {
  const raw = config.config.driver_questionnaire
  const sections: string[] = []
  for (const q of raw) if (!sections.includes(q.section)) sections.push(q.section)
  const questions: DriverQuestion[] = raw.map(q => ({
    question_id: q.question_id,
    driver: q.driver,
    section: q.section,
    prompt: q.prompt,
    help_text: q.help_text,
    ask_or_derive: q.ask_or_derive,
    max_points: q.options.reduce((m, o) => Math.max(m, o.points), 0),
    options: q.options,
    required: q.required,
  }))
  return {
    config_version: config.version,
    sections,
    max_total_score: config.max_total_driver_score,
    questions,
  }
}

export function baselineKnobs(config: ConfigResponse): Record<string, number> {
  const c = config.config
  const conv = c.convergence_rule as Record<string, number>
  const basisRule = c.earnings_basis_rule as Record<string, number>
  return {
    income_weight: c.triangulation_weights.income_weight,
    market_weight: c.triangulation_weights.market_weight,
    max_spread_pct: conv.max_spread_pct,
    low_band_pct: conv.low_band_pct,
    high_band_pct: conv.high_band_pct,
    crossover_earnings_threshold: basisRule.crossover_earnings_threshold,
    customer_concentration_trigger_pct: c.customer_concentration_trigger_pct,
  }
}

/**
 * VAL-OVERVIEW-PREFLIGHT-PARITY-1 — the engine fields that come from the saved
 * draft, in ONE place. `buildRecomputeBody` (Tuning, Sign-off, Intake) and
 * Overview's preflight body both spread this, so a saved input cannot reach
 * one screen's engine call and not the other's.
 */
export function draftEngineFields(draft: DraftInputs | null) {
  return {
    income_value: draft?.income_value ?? null,
    total_assets: draft?.total_assets ?? null,
    total_liabilities: draft?.total_liabilities ?? null,
    risk_free_rate: draft?.risk_free_rate ?? null,
    company_specific_risk: draft?.company_specific_risk ?? null,
    long_term_growth_rate: draft?.long_term_growth_rate ?? null,
    equity_risk_premium: draft?.equity_risk_premium ?? null,
    size_premium: draft?.size_premium ?? null,
    growth_rate: draft?.growth_rate ?? null,
    projection_years: draft?.projection_years ?? null,
    capitalized_earnings_weight: draft?.capitalized_earnings_weight ?? null,
    base_cash_flow: draft?.base_cash_flow ?? null,
    replacement_compensation: draft?.replacement_compensation ?? null,
    // 0 is the server's echo of "not entered" (the signed float shape is fixed); send blank.
    fair_value_adjustments: draft?.fair_value_adjustments || null,
    basis: draft?.basis ?? null,
    override_multiple: draft?.override_multiple ?? null,
    override_rationale: draft?.override_rationale ?? null,
    override_disposition: draft?.override_disposition ?? null,
    comp_multiples: draft?.comp_multiples ?? null,
  }
}

/**
 * VAL-FINALIZE-FROM-SAVED-1B — the saved draft (+ its saved tuning) in the
 * shape the Sign-off review panel reads. Display only: it is never sent to the
 * server, which reads the saved draft itself on preflight and finalize.
 */
export function bodyFromDraft(draft: DraftInputs, tuning: TuningBody | null): RecomputeBody {
  return {
    benchmark_key: draft.benchmark_key,
    normalized_earnings: draft.normalized_earnings,
    driver_answers: draft.answers,
    naics_code: draft.naics_code,
    sub_vertical: draft.sub_vertical,
    market_only_acknowledged: draft.market_only_acknowledged,
    ...draftEngineFields(draft),
    tuning: tuning ?? null,
  } as RecomputeBody
}

/**
 * The one body both /recompute and /preflight are called with.
 *
 * D6 — `income_value`/`total_assets`/`total_liabilities` come from the shared
 * draft, not from any field on this screen: Intake owns and writes them (it
 * has the asset-floor card), Tuning only reads them so the engine sees the
 * same balance-sheet figures either screen would send, instead of silently
 * omitting them here.
 */
export function buildRecomputeBody(args: {
  industry: ResolvedIndustry | null
  earningsOk: boolean
  earningsValue: number
  answers: Record<string, string>
  knobs: Record<string, number>
  baseline: Record<string, number>
  tuned: boolean
  rationale: string
  draft: DraftInputs | null
  acknowledgeMarketOnly: boolean
  /** VAL-INDUSTRY-WIRING-1 R6D — evidence only; see `RecomputeBody.comp_multiples`. */
  compMultiples?: number[] | null
}): RecomputeBody | null {
  const {
    industry, earningsOk, earningsValue, answers, knobs, baseline, tuned, rationale, draft,
    acknowledgeMarketOnly, compMultiples,
  } = args
  if (!industry || !earningsOk) return null
  const tuning = tuned
    ? ({
        ...(Object.fromEntries(
          Object.entries(knobs).filter(
            ([k, v]) => k !== 'market_weight' && Math.abs(v - baseline[k]) > 1e-9,
          ),
        ) as TuningKnobs),
        // The pair must sum to 1.0, so market always rides along with income.
        ...(Math.abs(knobs.income_weight - baseline.income_weight) > 1e-9
          ? { market_weight: Number((1 - knobs.income_weight).toFixed(4)) }
          : {}),
        rationale: rationale.trim() || undefined,
      })
    : null
  return {
    benchmark_key: industry.key,
    normalized_earnings: earningsValue,
    driver_answers: answers,
    ...draftEngineFields(draft),
    naics_code: industry.naicsCode,
    sub_vertical: industry.subVertical,
    // D6 — a recorded choice, not a disabled button. The server (finalize's
    // preflight) is what actually turns "not acknowledged" into a blocker on
    // a market-only run; this just carries the checkbox onto the wire.
    market_only_acknowledged: acknowledgeMarketOnly,
    // VAL-INPUT-PERSIST-1B B1/B2 — Tuning never passes its own comps
    // (ValuationTuning.tsx:200-210), so falling back to the draft's own
    // value here is what makes Tuning/Finalize see the comps Intake saved.
    comp_multiples: compMultiples !== undefined ? compMultiples : draftEngineFields(draft).comp_multiples,
    tuning,
    rationale: rationale.trim() || null,
  }
}
